import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  DatePicker,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  EditableValueCell,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  InlineValueTrigger,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  Text,
  toastManager,
  useIsMobile,
  type EditableValueCellAction,
  type EditableValueCellApi,
  type EditableValueCellEditor,
  type Omittable,
} from "@salestrak/ui";
import {
  getDefaultValueByType,
  validateCustomFieldValue,
} from "@salestrak/custom-fields";
import { UserSelector } from "#app/components/entity-selectors";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  FIELD_MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import {
  CustomFieldRenderer,
  CountryFlagImage,
  getCustomFieldDisplayValue,
  isCustomFieldValueEmpty,
} from "./custom-field-renderers";
import {
  getPhoneCountryIso,
  isMultipleField,
  toDateValue,
} from "#app/lib/custom-field-utils";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { normalizeRichTextValue } from "#app/lib/utils/rich-text-value";
import {
  blurActiveElement,
  buildCopyValueAction,
  getFieldTypeDescriptor,
  getProtocolHref,
  getValueActionText,
} from "#app/components/fields/field-type-descriptors";
import type { CustomFieldDef } from "./types";

/**
 * The custom-field VALUE cell: composes the library's `EditableValueCell`
 * (which owns activation, touch reveal, outside-click dismissal, validation
 * gating, value actions and reset) with this app's per-type editors, looked
 * up through the field-type descriptor registry. The issue view's field rows
 * and the issues table's custom-field columns both render this — the
 * per-type wiring lives here exactly once. The host owns dataToField/fieldDto
 * conversion: `value` arrives in field space and `onUpdate` fires in field
 * space.
 *
 * Commit routing mirrors what each editor can produce: the generic inline
 * renderers commit through the cell's VALIDATED path (free-typed input can be
 * invalid); overlay and in-place editors (date, select, member, checkbox,
 * photos, paragraph) write through `onUpdate` directly — their values are
 * constrained by the picker and cannot fail validation.
 */

export const CUSTOM_FIELD_VALUE_CELL_HEIGHT = 44;

// Snap points are fractions of the viewport height, the Drawer's scale.
const PARAGRAPH_SHEET_SNAP_POINTS = [0.5, 0.92];

export type CustomFieldValueCellApi = EditableValueCellApi;

export type CustomFieldValueCellProps = Omittable<{
  field: CustomFieldDef | undefined;
  /** Field-space value (already run through dataToField → fieldValue). */
  value: unknown;
  isActive?: boolean;
  /** Cell height in px; the photos grid treats it as a minimum. */
  height?: number;
  /** End-edge bleed into the host row's padding (the inline-ROW look).
   *  Table hosts pass false — a table cell clips the overhang square. */
  bleedEnd?: boolean;
  /** Host-tracked highlight: while false, overlays and touch reveal dismiss. */
  highlighted?: boolean;
  /** Fires with field-space values; `undefined` means "remove the value". */
  onUpdate: (value: unknown) => void;
  /** Any overlay (picker, popover, touch reveal) opened or closed. */
  onOverlayChange?: (open: boolean) => void;
  onInvalidStateChange?: (invalid: boolean) => void;
  /** Local validation error entering/leaving — feeds the host's label. */
  onValidationErrorChange?: (message: string | null) => void;
  // Photos-field delta writes (view mode only). Present → the renderer commits
  // each landed upload / removed tile as a single-photo add/remove instead of
  // a full-array overwrite, so concurrent uploads can't clobber each other.
  onAddPhoto?: (photoId: string) => void | Promise<void>;
  onRemovePhoto?: (photoId: string) => void | Promise<void>;
  onActivate?: () => void;
  onDeactivate?: () => void;
  /** Force-hide the value actions (e.g. while the host's row menu is up). */
  suppressActions?: boolean;
  // When true, the cell renders the display value with no hover, no
  // cursor pointer, no editor activation, and no edit/clear actions.
  // Used for view / comment access tiers.
  readOnly?: boolean;
  /** Prefixes photo download filenames in the lightbox. */
  downloadPrefix?: string;
  /** Host hook for "Reset value"-style chrome. */
  apiRef?: React.RefObject<CustomFieldValueCellApi | null>;
}>;

export function CustomFieldValueCell({
  field,
  value,
  isActive = false,
  height = CUSTOM_FIELD_VALUE_CELL_HEIGHT,
  bleedEnd = true,
  highlighted = false,
  onUpdate,
  onOverlayChange,
  onInvalidStateChange,
  onValidationErrorChange,
  onAddPhoto,
  onRemovePhoto,
  onActivate,
  onDeactivate,
  suppressActions = false,
  readOnly = false,
  downloadPrefix,
  apiRef,
}: CustomFieldValueCellProps) {
  const isMobile = useIsMobile();
  // Fullscreen photo viewer (photos fields' "fullscreen" value action).
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Paragraph draft state. The overlay editor renders from these refs;
  // `edited` gates commit so opening + closing a legacy value never dirties
  // it, and `open` mirrors the overlay state for the unmount flush below.
  const paragraphDraftRef = useRef("");
  const paragraphEditedRef = useRef(false);
  const paragraphOpenRef = useRef(false);

  // An open, edited paragraph draft is committed when the cell unmounts
  // (dialog closed, tab switched) — the close handler never got to run.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const fieldExistsRef = useRef(!!field);
  fieldExistsRef.current = !!field;
  useEffect(
    () => () => {
      if (
        paragraphOpenRef.current &&
        fieldExistsRef.current &&
        paragraphEditedRef.current
      ) {
        onUpdateRef.current(paragraphDraftRef.current);
      }
    },
    [],
  );

  const descriptor = field ? getFieldTypeDescriptor(field.type) : null;

  const displayValue = useMemo(
    () => (field ? getCustomFieldDisplayValue(field, value) : "-"),
    [field, value],
  );
  const hasValue = useMemo(
    () => (field ? !isCustomFieldValueEmpty(field, value) : false),
    [field, value],
  );
  const phoneCountryIso =
    field?.type === "phone" && hasValue ? getPhoneCountryIso(value) : null;

  const valueActionText = useMemo(
    () => (field ? getValueActionText(field, value, displayValue) : null),
    [displayValue, field, value],
  );
  const protocolHref = useMemo(
    () => (field && hasValue ? getProtocolHref(field, valueActionText) : null),
    [field, hasValue, valueActionText],
  );

  const validate = useCallback(
    (next: unknown): string | null => {
      if (!field) return null;
      const result = validateCustomFieldValue(field, next);
      return result.valid ? null : (result.message ?? `Invalid ${field.name}`);
    },
    [field],
  );

  const handleInvalidCommit = useCallback((message: string) => {
    toastManager.add({ title: message, type: "error" });
  }, []);

  // Opens the protocol (mailto / tel / website) for protocol fields. Wired to
  // a dedicated action button — the cell click itself always edits. No overlay
  // opens here, so this must NOT report through onOverlayChange: nothing would
  // ever emit the matching close and the host's highlight would stick.
  const handleOpenProtocol = useCallback(() => {
    if (!field || !protocolHref || typeof window === "undefined") return;
    blurActiveElement();
    if (field.type === "url") {
      window.open(protocolHref, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = protocolHref;
    }
  }, [field, protocolHref]);

  const actions = useMemo<EditableValueCellAction[]>(() => {
    if (!field || !descriptor || !hasValue) return [];
    const noun = descriptor.actionNoun;
    const list: EditableValueCellAction[] = [];
    for (const action of descriptor.actions) {
      switch (action) {
        case "open":
          // Drop the protocol action when the value can't form a valid href.
          if (!protocolHref) break;
          list.push({
            key: "open",
            icon: "external-link",
            label: `${descriptor.protocolVerb ?? "Open"} ${noun}`,
            onSelect: handleOpenProtocol,
          });
          break;
        case "copy":
          list.push(buildCopyValueAction(noun, () => valueActionText));
          break;
        case "fullscreen":
          list.push({
            key: "fullscreen",
            icon: "maximize-2",
            label: `View ${noun} full screen`,
            onSelect: () => setLightboxOpen(true),
          });
          break;
        case "clear":
          list.push({
            key: "clear",
            icon: "x",
            label: `Clear ${noun}`,
            // Clearing writes the type's empty default as a real value (not
            // the `undefined` remove signal), skipping validation — the
            // default is safe by construction.
            onSelect: (cell) =>
              cell.overwrite(getDefaultValueByType(field.type) ?? null),
          });
          break;
      }
    }
    // Read-only viewers can still copy the value — strip edit / clear
    // actions but keep `copy`.
    return readOnly ? list.filter((a) => a.key === "copy") : list;
  }, [
    descriptor,
    field,
    handleOpenProtocol,
    hasValue,
    protocolHref,
    readOnly,
    valueActionText,
  ]);

  if (!field || !descriptor) {
    return (
      <Box fill="flex" minWidth={0} direction="row" align="stretch">
        <Box fill="flex" justify="center" paddingX="md" paddingY="sm">
          <Text size="sm" variant="muted">
            {"—"}
          </Text>
        </Box>
      </Box>
    );
  }

  const isPhotosField = descriptor.editor === "photos";
  const mediaIds = isPhotosField
    ? (Array.isArray(value) ? value : []).filter(
        (v): v is string => typeof v === "string",
      )
    : [];

  const display =
    isPhotosField && readOnly ? (
      <Box
        fill="flex"
        direction="row"
        wrap
        gap="sm"
        paddingY="xs"
        paddingX="md"
        align="center"
      >
        {mediaIds.map((photoId, index) => (
          <ResolvedMediaTile
            key={`${photoId}-${index}`}
            photoId={photoId}
            size={FIELD_MEDIA_TILE_SIZE}
          />
        ))}
      </Box>
    ) : (
      <Box
        fill="flex"
        direction="row"
        align="center"
        gap="xs"
        paddingX="md"
        height={height}
      >
        {phoneCountryIso ? (
          <CountryFlagImage iso={phoneCountryIso} size={18} />
        ) : null}
        <Box fill="flex">
          <Text
            size="sm"
            variant={displayValue === "-" ? "muted" : undefined}
            truncate={1}
          >
            {displayValue}
          </Text>
        </Box>
      </Box>
    );

  const buildEditor = (): EditableValueCellEditor => {
    switch (descriptor.editor) {
      // Checkbox toggles in place — no edit mode. The box always renders,
      // even unset (null), rather than the '━' empty marker. First click
      // writes `true`; further clicks toggle true/false.
      case "checkbox":
        return {
          kind: "content",
          render: () => (
            <InlineValueTrigger
              aria-label={`Toggle ${field.name}`}
              onClick={() => onUpdate(!(value === true))}
            >
              <Box direction="row" align="center" justify="start" paddingX="md">
                {/* Visual-only: the InlineValueTrigger button owns the toggle —
                    an interactive Checkbox would nest a button inside it, so this
                    one is disabled with its pointer events off. */}
                <Checkbox
                  checked={value === true}
                  disabled
                  className="pointer-events-none opacity-100"
                />
              </Box>
            </InlineValueTrigger>
          ),
        };

      // Photos edit in place — thumbnails live directly in the cell (no
      // text summary, no activation step); the row grows with the grid.
      case "photos":
        return {
          kind: "content",
          render: () => (
            <>
              <CustomFieldRenderer
                field={field}
                value={value}
                onChangeValue={(next) => onUpdate(next)}
                onAddPhoto={onAddPhoto}
                onRemovePhoto={onRemovePhoto}
              />
              <MediaLightbox
                media={mediaIds.map((id) => ({ id, contentType: "image/*" }))}
                open={lightboxOpen}
                onOpenChange={setLightboxOpen}
                title={field.name}
                downloadPrefix={downloadPrefix}
              />
            </>
          ),
        };

      case "date":
        return {
          kind: "overlay",
          render: ({ open, onOpenChange, cellWithTriggerPart }) => (
            <DatePicker
              mode={descriptor.pickerMode ?? "date"}
              value={toDateValue(value)}
              onValueChange={(next) => onUpdate(next)}
              open={open}
              onOpenChange={onOpenChange}
              align="start"
            >
              {cellWithTriggerPart(<PopoverTrigger />)}
            </DatePicker>
          ),
        };

      case "select": {
        const selectOptions = (field.config?.options ?? []).map((opt) => ({
          value: opt.value ?? opt.label ?? "",
          label: opt.label ?? opt.value ?? "",
        }));
        const isMultiSelect = isMultipleField(field.config);
        // Select values are always arrays; single-mode reads the first
        // element. Base UI's Select is value-keyed: the selected value is the
        // bare option string (labels ride on the items), so a stored value
        // with no matching option is still passed through as-is rather than
        // fabricating an option.
        const singleSelectValue = (() => {
          if (isMultiSelect) return null;
          const first = Array.isArray(value) ? value[0] : value;
          return first == null ? null : String(first);
        })();
        const multiSelectValues =
          isMultiSelect && Array.isArray(value)
            ? (value as string[]).map(String)
            : [];
        return {
          kind: "overlay",
          render: ({ open, onOpenChange, cellWithTriggerPart }) => {
            // `chevron={false}` keeps the InlineValueTrigger's own chrome. The
            // default appends the chevron AND the stock trigger's border, fill
            // and shadow, so a select row read as a boxed form control while
            // every text, date and member row beside it stayed a bare cell.
            const trigger = cellWithTriggerPart(<SelectTrigger chevron={false} />);
            // DESIGN-TUNABLE: Base UI's Select is not a combobox and has no
            // search slot, so the option list is unfiltered.
            const content = (
              <SelectPopup align="start">
                {selectOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            );
            return isMultiSelect ? (
              <Select
                multiple
                items={selectOptions}
                value={multiSelectValues}
                onValueChange={(vals: string[]) => onUpdate(vals)}
                open={open}
                onOpenChange={onOpenChange}
              >
                {trigger}
                {content}
              </Select>
            ) : (
              <Select
                items={selectOptions}
                value={singleSelectValue}
                onValueChange={(next: string | null) =>
                  onUpdate(next ? [next] : [])
                }
                open={open}
                onOpenChange={onOpenChange}
              >
                {trigger}
                {content}
              </Select>
            );
          },
        };
      }

      case "member": {
        // Member values are always id arrays; single-mode caps at one element.
        const isSingleMember = !isMultipleField(field.config);
        return {
          kind: "overlay",
          // UserSelector runs the workspace-users pool (a query per
          // instance) and this cell renders once per visible row in a
          // virtualized table — mount it only while its overlay is open. At
          // rest the library cell renders a plain trigger; the member
          // display is a count, no pool needed.
          defer: true,
          render: ({ open, onOpenChange, cellWithTriggerPart }) => (
            <UserSelector
              value={value as string | string[] | null}
              onValueChange={(ids) =>
                onUpdate(isSingleMember ? (ids[0] ?? null) : ids)
              }
              maxSelections={isSingleMember ? 1 : undefined}
              open={open}
              onOpenChange={onOpenChange}
            >
              {cellWithTriggerPart(<EntitySelectorTrigger />)}
              <EntitySelectorContent size="sm" align="start">
                <EntitySelectorInput />
                <EntitySelectorPool value={USER_POOL_ID} />
              </EntitySelectorContent>
            </UserSelector>
          ),
        };
      }

      case "paragraph":
        return {
          kind: "overlay",
          render: (api) => {
            // Mirror the overlay state for the unmount flush: a silent
            // dismissal (host highlight moved) must not flush the draft it
            // discarded.
            paragraphOpenRef.current = api.open;
            const openParagraph = () => {
              // A false return means the touch gate revealed the value
              // actions instead — the draft setup must not run.
              if (!api.onOpenChange(true)) return;
              // Display-only normalize; reset the edited flag so a no-op
              // open/close never persists it.
              paragraphDraftRef.current = normalizeRichTextValue(value);
              paragraphEditedRef.current = false;
              api.deactivate();
            };
            const closeParagraph = () => {
              if (paragraphEditedRef.current) {
                onUpdate(paragraphDraftRef.current);
              }
              blurActiveElement();
              api.onOpenChange(false);
            };
            const handleOpenChange = (next: boolean) =>
              next ? openParagraph() : closeParagraph();
            const editorNode = (
              <RichTextEditor
                compact
                mentions
                autoFocus
                content={paragraphDraftRef.current}
                placeholder="Write"
                shortcutHints
                // onUpdate fires only on real user edits (never on content
                // load) — mark dirty.
                onUpdate={(html) => {
                  paragraphDraftRef.current = html;
                  paragraphEditedRef.current = true;
                }}
                contentPaddingX="sm"
                contentPaddingY="sm"
              />
            );
            // Explicit branch: a Drawer on mobile, a Popover on desktop. Only
            // the Drawer carries a title. Base UI coordinates nested layers
            // itself, so there is nothing to configure for stacking.
            if (isMobile) {
              return (
                <>
                  {api.cellWithTrigger(openParagraph)}
                  <Drawer
                    open={api.open}
                    onOpenChange={handleOpenChange}
                    // Fixed snap points: a content-driven sheet would lurch as
                    // the unbounded body grows.
                    snapPoints={PARAGRAPH_SHEET_SNAP_POINTS}
                  >
                    <DrawerPopup showBar>
                      <DrawerHeader>
                        <DrawerTitle>{field.name}</DrawerTitle>
                      </DrawerHeader>
                      <DrawerPanel className="px-0">{editorNode}</DrawerPanel>
                    </DrawerPopup>
                  </Drawer>
                </>
              );
            }
            return (
              <Popover open={api.open} onOpenChange={handleOpenChange}>
                {api.cellWithTriggerPart(<PopoverTrigger />)}
                {/* Width and the flush inset are class recipes — the popover
                    viewport owns its padding and no prop reaches it. */}
                <PopoverPopup
                  align="start"
                  side="bottom"
                  padding="none"
                  className="w-[420px]"
                >
                  {editorNode}
                </PopoverPopup>
              </Popover>
            );
          },
        };

      // Generic inline editing (text, number, email, phone, url, money,
      // address, and unknown types): the renderer commits through the cell's
      // VALIDATED path and deactivates on blur.
      default:
        return {
          kind: "inline",
          render: ({ commit, deactivate, invalid }) => (
            <CustomFieldRenderer
              field={field}
              value={value}
              onChangeValue={commit}
              isActive
              onDeactivate={deactivate}
              autoFocus
              invalid={invalid}
            />
          ),
        };
    }
  };

  return (
    <EditableValueCell
      value={value}
      display={display}
      hasValue={hasValue}
      editor={readOnly ? undefined : buildEditor()}
      triggerLabel={`Edit ${field.name}`}
      height={height}
      grow={isPhotosField}
      bleedEnd={bleedEnd}
      readOnly={readOnly}
      actions={actions}
      suppressActions={suppressActions}
      validate={validate}
      onCommit={onUpdate}
      onInvalidCommit={handleInvalidCommit}
      onInvalidChange={onInvalidStateChange}
      onValidationErrorChange={onValidationErrorChange}
      isActive={isActive}
      onActivate={onActivate}
      onDeactivate={onDeactivate}
      highlighted={highlighted}
      onOverlayChange={onOverlayChange}
      apiRef={apiRef}
    />
  );
}
